/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_SUCCESS,
  Button,
  DialogContentProps,
  FORM_THEME_LITE,
  Form,
  TOAST_TYPE_SUCCESS,
  TextField,
  TextFieldProps,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { DirectoryViewer } from 'apps/digital-asset-management/src/Components/DirectoryViewer/DirectoryViewer';
import { DirectoryViewerItem } from 'apps/digital-asset-management/src/Components/DirectoryViewer/DirectoryViewer.props';
import { useFileStore } from 'apps/digital-asset-management/src/Services/file-store';
import {
  UpdateFileStoreMappingDto,
  getFileStoreMappingsService,
} from 'apps/digital-asset-management/src/Services/file-store-mappings';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { PathURLValidator } from '../../../../Utilities/PathUrlValidator/PathUrlValidator';
import { STATIC_SITES_DATA_IDS, STATIC_SITES_FORM_IDS } from '../../StaticSites.constants';
import { EditRouteFormProps } from './EditRouteForm.props';

/**
 * The form for editing an existing route.
 */
export const EditRouteForm = (dialogProps: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();

  const { close, setAfterClose, setButtonKeys, setButtons } = dialogProps;
  const { address, clearCache, id, siteUrl, source: repositoryPath } = dialogProps.customProps as EditRouteFormProps;

  // Services
  const { listDirectories } = useFileStore();
  const fileStoreMappingsService = getFileStoreMappingsService();
  const { updateActiveToast } = useToastStore();

  // State
  const [repositoryContents, setRepositoryContents] = useState<Array<DirectoryViewerItem> | undefined>(undefined);
  const [selectedRepositoryPath, setSelectedRepositoryPath] = useState<string | null>(repositoryPath);
  const [repositoryError, setRepositoryError] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  // Form refs
  const siteUrlRef = useRef<HTMLInputElement>(null);
  const pathUrlRef = useRef<HTMLInputElement>(null);

  // Labels
  const labels = useMemo(() => {
    return {
      cancel: t(CONFIGURATION_STRINGS.cancel),
      domainEditNote: t(CONFIGURATION_STRINGS.domainEditNote),
      editRoute: t(CONFIGURATION_STRINGS.editRoute),
      errorUnknown: t(CONFIGURATION_STRINGS.errorUnknown),
      invalidPathUrl: t(CONFIGURATION_STRINGS.invalidPathUrl),
      pathUrl: t(CONFIGURATION_STRINGS.pathUrl),
      pathUrlNote: t(CONFIGURATION_STRINGS.pathUrlNote),
      repositoryPath: t(CONFIGURATION_STRINGS.repositoryPath),
      requiredField: t(CONFIGURATION_STRINGS.requiredField),
      routeUpdated: t(CONFIGURATION_STRINGS.routeUpdated),
      save: t(CONFIGURATION_STRINGS.save),
      staticSiteURL: t(CONFIGURATION_STRINGS.staticSiteURL),
    };
  }, [t]);

  // Validators
  const pathURLValidator = useMemo(() => {
    return new PathURLValidator<boolean>((): boolean => false);
  }, []);

  // Validation
  const validatePathUrl = useCallback((): boolean => {
    const nextErrors = { ...fieldErrors };

    let isValid = true;
    const pathUrl = (pathUrlRef.current as HTMLInputElement).value;

    if (!pathURLValidator.validate(String(pathUrl))) {
      isValid = false;
      nextErrors[STATIC_SITES_FORM_IDS.pathUrl] = labels.invalidPathUrl;
    } else {
      delete nextErrors[STATIC_SITES_FORM_IDS.pathUrl];
    }

    setFieldErrors(nextErrors);
    return isValid;
  }, [fieldErrors, labels, pathURLValidator, setFieldErrors]);

  const validateRepositoryPath = useCallback((): boolean => {
    let isValid = true;

    if (!selectedRepositoryPath) {
      isValid = false;
      setFormError(labels.requiredField);
    }

    return isValid;
  }, [labels, selectedRepositoryPath, setFormError]);

  const validateFields = useCallback((): void => {
    validatePathUrl();
    validateRepositoryPath();
  }, [validatePathUrl, validateRepositoryPath]);

  const customClose = useCallback(() => {
    // Refresh mapping list on dialog close
    clearCache(new Date().toISOString());
  }, [clearCache]);

  const submitHandler = useCallback(() => {
    setIsBusy(true);
    setFormError('');

    // Set up inputs
    const inputs = {
      pathUrl: pathUrlRef.current as HTMLInputElement,
      siteUrl: siteUrlRef.current as HTMLInputElement,
    };

    // Collect the current values of the form
    const siteUrl = inputs.siteUrl.value;
    const pathUrl = inputs.pathUrl.value;
    const repositoryPath = selectedRepositoryPath;

    if (!pathURLValidator.validate(String(pathUrl)) || !repositoryPath) {
      validateFields();
      setSubmit(false);
      setIsBusy(false);
      return;
    }

    const dto: UpdateFileStoreMappingDto = {
      address: siteUrl + pathUrl,
      id,
      source: repositoryPath,
    };

    const successToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: labels.routeUpdated,
      toastType: TOAST_TYPE_SUCCESS,
    };

    lastValueFrom(fileStoreMappingsService.updateMapping(dto))
      .then(() => {
        updateActiveToast(<Toast {...successToastProps} data-id={STATIC_SITES_DATA_IDS.editToastSuccess} />);
        return close();
      })
      .catch((err) => {
        setFormError(labels.errorUnknown);
        setSubmit(false);
        setIsBusy(false);
      });
  }, [
    close,
    fileStoreMappingsService,
    id,
    labels,
    pathURLValidator,
    selectedRepositoryPath,
    setFormError,
    setIsBusy,
    setSubmit,
    updateActiveToast,
    validateFields,
  ]);

  useEffect(() => {
    const cancelButton = (
      <Button
        data-id={STATIC_SITES_DATA_IDS.actionEditRouteCancel}
        disabled={isBusy}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );
    const saveButton = (
      <Button
        data-id={STATIC_SITES_DATA_IDS.actionEditRouteConfirm}
        disabled={isBusy}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={labels.save}
        theme={BUTTON_THEME_SUCCESS}
      />
    );

    setAfterClose && setAfterClose(() => customClose);

    // Form submission check
    if (isFormSubmitted) {
      setButtonKeys([`save`]);
      setButtons([saveButton]);

      if (!isBusy) {
        submitHandler();
      }
    } else {
      setButtonKeys([`cancel`, `save`]);
      setButtons([cancelButton, saveButton]);
    }
  }, [
    close,
    customClose,
    isBusy,
    isFormSubmitted,
    labels,
    setAfterClose,
    setButtonKeys,
    setButtons,
    setSubmit,
    submitHandler,
  ]);

  // Get repository contents for directory viewer (a tree structure showing the selected directory)
  useEffect(() => {
    const fetchDirectoryTree = async (): Promise<void> => {
      try {
        const rootDirectory: DirectoryViewerItem = {
          children: [],
          id: 'root',
          path: '/',
          type: 'DIRECTORY',
          urls: [],
        };

        // Loop through each directory in the repository path and add its child directories to the tree
        let directoryNode: DirectoryViewerItem | undefined;

        for (let i = 0; i < repositoryPath.length; i++) {
          if (repositoryPath[i] === '/') {
            const directoryPath = repositoryPath.substring(0, i + 1);

            // On first pass, set node to the root directory
            // Otherwise, set node to child directory that matches the repository path
            if (!directoryNode) {
              rootDirectory.path = directoryPath;
              directoryNode = rootDirectory;
            } else if (directoryNode.children) {
              directoryNode = directoryNode.children.find((item) => item.path === directoryPath);
            }

            // If no matching node, it means directory no longer exists in file store
            // In that case, stop generating the tree
            if (!directoryNode) {
              break;
            }

            // Get the child directories and add them to directory node
            const response = await listDirectories({ source: directoryPath });
            directoryNode.children = response.data ?? [];
          }
        }

        setRepositoryContents(rootDirectory.children as Array<DirectoryViewerItem>);
      } catch (e) {
        // istanbul ignore next
        setRepositoryError(true);
      }
    };

    if (!repositoryContents) {
      fetchDirectoryTree();
    }
  }, [listDirectories, repositoryContents, repositoryPath, setRepositoryContents, setRepositoryError]);

  // Static site URL
  const siteUrlFieldProps: TextFieldProps = {
    disabled: true,
    fieldLabel: labels.staticSiteURL,
    id: STATIC_SITES_FORM_IDS.staticSiteUrl,
    name: STATIC_SITES_FORM_IDS.staticSiteUrl,
    note: labels.domainEditNote,
    required: true,
    value: siteUrl,
  };

  // Path URL
  const pathUrlFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[STATIC_SITES_FORM_IDS.pathUrl] || null,
    fieldLabel: labels.pathUrl,
    id: STATIC_SITES_FORM_IDS.pathUrl,
    name: STATIC_SITES_FORM_IDS.pathUrl,
    note: labels.pathUrlNote,
    onChange: (): void => {
      validatePathUrl();
    },
    value: address.replace(siteUrl, ''),
  };

  return (
    <div className="manage-routing__form">
      <Form errorMessage={formError} legend={labels.editRoute} theme={FORM_THEME_LITE}>
        <TextField {...siteUrlFieldProps} data-id={STATIC_SITES_DATA_IDS.staticSiteUrl} ref={siteUrlRef} />
        <TextField {...pathUrlFieldProps} data-id={STATIC_SITES_DATA_IDS.pathUrl} ref={pathUrlRef} />

        <div className="manage-routing__form-field" data-id={STATIC_SITES_DATA_IDS.repositoryPath}>
          <span className="manage-routing__form-label">{labels.repositoryPath}</span>
          <DirectoryViewer
            error={repositoryError}
            initialData={repositoryContents}
            setValue={setSelectedRepositoryPath}
            value={selectedRepositoryPath}
          />
        </div>
      </Form>
    </div>
  );
};
